/**
 * Iter 245 — Tableau de bord Commandes & Stocks.
 *
 * Vue d'ensemble : KPIs + actions rapides + alertes.
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import {
  Package, ShoppingCart, AlertTriangle, TrendingUp, Truck,
  CheckCircle2, Bell, Loader2, RefreshCw, ArrowRight,
} from "lucide-react";

const Card = ({ icon: Icon, label, value, color, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="text-left bg-[#14101c] border border-white/5 hover:border-[#f7c935]/40 rounded-xl p-5 transition-all"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}20`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      {onClick && <ArrowRight className="w-4 h-4 text-slate-600" />}
    </div>
    <div className="text-3xl font-bold text-white">{value}</div>
    <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{label}</div>
  </button>
);

export default function InventoryDashboardPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/dashboard");
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;
  }

  const d = data || {};
  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6" data-testid="inventory-dashboard">
      <PageHeader
        icon={<Package />}
        title="Commandes & Stocks"
        description="Vue d'ensemble du module : catalogue, stocks, fournisseurs et commandes."
        actions={
          <Button onClick={load} variant="ghost" size="sm" className="text-slate-400">
            <RefreshCw className="w-4 h-4 mr-2" /> Rafraîchir
          </Button>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card icon={Package} label="Produits actifs" value={d.products_count || 0} color="#3b82f6" testid="kpi-products" onClick={() => navigate("/admin/inventory/products")} />
        <Card icon={AlertTriangle} label="Stocks bas" value={d.low_stock_count || 0} color="#ef4444" testid="kpi-low-stock" onClick={() => navigate("/admin/inventory/products")} />
        <Card icon={Bell} label="Suggestions réappro" value={d.suggestions_pending || 0} color="#f7c935" testid="kpi-suggestions" onClick={() => navigate("/admin/inventory/restock")} />
        <Card icon={Truck} label="PO en cours" value={d.po_pending || 0} color="#a855f7" testid="kpi-po-pending" onClick={() => navigate("/admin/inventory/purchase-orders")} />
        <Card icon={CheckCircle2} label="PO reçues à payer" value={d.po_received_not_paid || 0} color="#10b981" testid="kpi-po-to-pay" onClick={() => navigate("/admin/inventory/purchase-orders")} />
        <Card icon={ShoppingCart} label="Commandes aujourd'hui" value={d.orders_today || 0} color="#0ea5e9" testid="kpi-orders-today" onClick={() => navigate("/admin/inventory/customer-orders")} />
        <Card icon={TrendingUp} label="CA 30j (EUR)" value={`${(d.revenue_30d_eur || 0).toLocaleString("fr-FR")} €`} color="#22c55e" testid="kpi-revenue" />
      </div>

      {/* Actions rapides */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-3">Actions rapides</h2>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => navigate("/admin/inventory/products")} variant="outline" size="sm" data-testid="quick-products">
            <Package className="w-4 h-4 mr-2" /> Catalogue
          </Button>
          <Button onClick={() => navigate("/admin/inventory/suppliers")} variant="outline" size="sm" data-testid="quick-suppliers">
            <Truck className="w-4 h-4 mr-2" /> Fournisseurs
          </Button>
          <Button onClick={() => navigate("/admin/inventory/purchase-orders")} variant="outline" size="sm" data-testid="quick-po">
            <ShoppingCart className="w-4 h-4 mr-2" /> Commandes fournisseur
          </Button>
          <Button onClick={() => navigate("/admin/inventory/customer-orders")} variant="outline" size="sm" data-testid="quick-customer-orders">
            <ShoppingCart className="w-4 h-4 mr-2" /> Commandes clients
          </Button>
          <Button onClick={() => navigate("/admin/inventory/restock")} variant="outline" size="sm" data-testid="quick-restock">
            <Bell className="w-4 h-4 mr-2" /> Suggestions de réappro
          </Button>
          <Button onClick={() => navigate("/admin/inventory/shopify")} variant="outline" size="sm" data-testid="quick-shopify">
            <ShoppingCart className="w-4 h-4 mr-2" /> Config Shopify
          </Button>
        </div>
      </div>

      {(d.suggestions_pending || 0) > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between" data-testid="suggestions-alert">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <div>
              <div className="text-sm font-bold text-white">{d.suggestions_pending} suggestion(s) de réapprovisionnement en attente</div>
              <div className="text-xs text-slate-400">Certains stocks ont atteint le seuil de réappro. Validez les commandes en 1 clic.</div>
            </div>
          </div>
          <Button onClick={() => navigate("/admin/inventory/restock")} size="sm" className="bg-amber-500 hover:bg-amber-400 text-[#14101c]">
            Voir les suggestions
          </Button>
        </div>
      )}
    </div>
  );
}
